import React, { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import "./UpdateMultipleFieldsAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";

const normalizeFieldIdentifier = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

// Check if a field is the "Other On Hold Reason" field
const isOtherOnHoldReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    normalizedFieldName.includes("otheronholdreason") ||
    normalizedLabel.includes("otheronholdreason")
  );
};

// Check if a field is the "On Hold Reason" field (but not Other)
const isOnHoldReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    (normalizedFieldName.includes("onholdreason") ||
      normalizedLabel.includes("onholdreason")) &&
    !isOtherOnHoldReasonField(field)
  );
};

// Check if a field is the "Other Cancellation Reason" field
const isOtherCancellationReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    normalizedFieldName.includes("othercancellationreason") ||
    normalizedLabel.includes("othercancellationreason")
  );
};

// Check if a field is the "Cancellation Reason" field (but not Other)
const isCancellationReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    (normalizedFieldName.includes("cancellationreason") ||
      normalizedLabel.includes("cancellationreason")) &&
    !isOtherCancellationReasonField(field)
  );
};

// Check if a field is the "Other Cleansing Tool" field
const isOtherCleansingToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    normalizedFieldName.includes("othercleansingtool") ||
    normalizedLabel.includes("othercleansingtool")
  );
};

// Check if a field is the "Cleansing Tool" field
const isCleansingToolField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    (normalizedFieldName.includes("cleansingtool") ||
      normalizedLabel.includes("cleansingtool")) &&
    !isOtherCleansingToolField(field)
  );
};

// Check if a field is the "Tool Issue Description" field
const isToolIssueDescriptionField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    normalizedFieldName.includes("toolissuedescription") ||
    normalizedLabel.includes("toolissuedescription")
  );
};

// Check if a field is the "Tool Issue Type" field (but not Description)
const isToolIssueTypeField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    (normalizedFieldName.includes("toolissuetype") ||
      normalizedLabel.includes("toolissuetype")) &&
    !isToolIssueDescriptionField(field)
  );
};

// Check if a field is the "Reprocess Description" field
const isReprocessDescriptionField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    normalizedFieldName.includes("reprocessdescription") ||
    normalizedLabel.includes("reprocessdescription")
  );
};

// Check if a field is the "Reprocess Reason" field (but not Description)
const isReprocessReasonField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(field?.fieldKey);
  const normalizedLabel = normalizeFieldIdentifier(field?.label);

  return (
    (normalizedFieldName.includes("reprocessreason") ||
      normalizedLabel.includes("reprocessreason")) &&
    !isReprocessDescriptionField(field)
  );
};

export const UpdateMultipleFieldsAICard = ({
  message,
  onActionClick,
  orderDetails,
}) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  const currentUserRole = useMemo(() => {
    const roleSource =
      selectedRole?.roleName ||
      selectedRole?.name ||
      (typeof selectedRole === "string" ? selectedRole : "") ||
      user?.role ||
      "";

    return String(roleSource).trim();
  }, [selectedRole, user?.role]);

  const normalizedCurrentUserRole = useMemo(
    () => currentUserRole.toUpperCase(),
    [currentUserRole],
  );

  const isDidRole = useMemo(
    () => !normalizedCurrentUserRole || normalizedCurrentUserRole === "DID",
    [normalizedCurrentUserRole],
  );

  const isRegionalLead = useMemo(
    () => normalizedCurrentUserRole === "REGIONAL LEAD",
    [normalizedCurrentUserRole],
  );

  const now = new Date();
  const day = now.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  // Extract editable fields from orderDetails for Main Details, Dates, Contributors, Additional Details, Efforts and Savings sections
  const { mainFields, dateFields, contributorFields, additionalFields } =
    useMemo(() => {
      if (!orderDetails || !Array.isArray(orderDetails)) {
        return {
          mainFields: [],
          dateFields: [],
          contributorFields: [],
          additionalFields: [],
        };
      }

      const isFieldEditable = (field) => {
        if (!field || field.isNonEditableByDA === true) {
          return false;
        }

        if (isRegionalLead) {
          if (typeof field?.isEditableByRL === "boolean") {
            return field.isEditableByRL;
          }

          return true;
        }

        if (isDidRole) {
          if (typeof field?.isEditable === "boolean") {
            return field.isEditable;
          }

          if (typeof field?.editable === "boolean") {
            return field.editable;
          }
        }

        return true;
      };

      const getEditableFields = (sectionName) => {
        const section = orderDetails.find((tab) => tab.section === sectionName);
        if (!section || !section.pagefields) return [];
        //        section.pagefields.filter(field => field.isEditable === true && field.isNonEditableByDA === false).
        return section.pagefields.filter(isFieldEditable).map((field) => ({
          label: field.labelToShowOnUI || field.field,
          fieldKey: field.field,
          dataType: field.dataType,
          fieldValueList: field.fieldValueList || [],
        }));
      };

      // Fields that should not be editable from chat even if isEditable is true
      const nonChatEditableContributorFields = ["preparer", "reviewer", "approver"];

      return {
        mainFields: getEditableFields("Main Details"),
        dateFields: getEditableFields("Dates"),
        contributorFields: getEditableFields("Contributors").filter((field) => {
          const normalizedFieldKey = normalizeFieldIdentifier(field.fieldKey);
          const normalizedLabel = normalizeFieldIdentifier(field.label);
          return !nonChatEditableContributorFields.some(
            (excluded) =>
              normalizedFieldKey.includes(excluded) ||
              normalizedLabel.includes(excluded)
          );
        }),
        additionalFields: getEditableFields("Additional Details"),
      };
      // }, [orderDetails]);
    }, [isDidRole, isRegionalLead, orderDetails]);

  // Combine all fields for easy lookup of dependent fields
  const allFields = useMemo(() => {
    return [
      ...mainFields,
      ...dateFields,
      ...contributorFields,
      ...additionalFields,
    ];
  }, [mainFields, dateFields, contributorFields, additionalFields]);

  // Find dependent "Other" field for a given primary field
  const findDependentOtherField = useCallback((field) => {
    // If this is On Hold Reason, find Other On Hold Reason
    if (isOnHoldReasonField(field)) {
      return allFields.find((f) => isOtherOnHoldReasonField(f)) || null;
    }
    // If this is Cancellation Reason, find Other Cancellation Reason
    if (isCancellationReasonField(field)) {
      return allFields.find((f) => isOtherCancellationReasonField(f)) || null;
    }
    // If this is Cleansing Tool, find Other Cleansing Tool
    if (isCleansingToolField(field)) {
      return allFields.find((f) => isOtherCleansingToolField(f)) || null;
    }
    // If this is Tool Issue Type, find Tool Issue Description
    if (isToolIssueTypeField(field)) {
      return allFields.find((f) => isToolIssueDescriptionField(f)) || null;
    }
    // If this is Reprocess Reason, find Reprocess Description
    if (isReprocessReasonField(field)) {
      return allFields.find((f) => isReprocessDescriptionField(f)) || null;
    }
    return null;
  }, [allFields]);

  const handleCheckboxChange = (field) => {
    setSelectedFields((prev) => {
      const isSelected = prev.some((f) => f.fieldKey === field.fieldKey);
      if (isSelected) {
        return prev.filter((f) => f.fieldKey !== field.fieldKey);
      } else {
        return [...prev, field];
      }
    });
  };

  const isFieldSelected = (field) => {
    return selectedFields.some((f) => f.fieldKey === field.fieldKey);
  };

  const handleDoneClick = () => {
    if (selectedFields.length === 0 || !onActionClick) return;

    // Build final fields list with dependent "Other" fields included
    const fieldsWithDependents = [...selectedFields];
    const addedFieldKeys = new Set(selectedFields.map((f) => f.fieldKey));

    selectedFields.forEach((field) => {
      const dependentField = findDependentOtherField(field);
      if (dependentField && !addedFieldKeys.has(dependentField.fieldKey)) {
        fieldsWithDependents.push(dependentField);
        addedFieldKeys.add(dependentField.fieldKey);
      }
    });

    onActionClick({ action: "Action Taken", selectedFields: fieldsWithDependents });
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="order-ai-card update-multiple-fields-card">
        <div className="order-ai-card-header">
          <div className="order-ai-card-title-logo">
            <img
              src={aiChatlogo}
              alt="LOGO"
              aria-hidden="true"
              className="order-ai-logo"
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="order-ai-card-title-Avatar">
            <span>{message.header?.title || "Axon AI"}</span>
            <div className="order-ai-card-time">{`${day} ${time}`}</div>
          </div>
        </div>

        <div className="order-ai-card-body">
          <div className="update-order-info-header">
            <p className="update-order-info-text">
              Ok, Select the fields you want to update.
            </p>
          </div>

          <div className="update-order-info-instruction">
            <span className="emoji-icon">👇</span>
            {"  "}
            <p className="update-order-info-instruction-text">
              Select fields to proceed.
            </p>
          </div>
          <div className="update-order-section-line" />

          <div className="update-multiple-fields-sections">
            {mainFields.length > 0 && (
              <div className="update-multiple-fields-section">
                <h3 className="section-title">Main</h3>
                <div className="checkbox-list">
                  {mainFields.map((field, idx) => (
                    <div
                      className="checkbox-item-container"
                      key={field.fieldKey || idx}
                    >
                      <input
                        type="checkbox"
                        checked={isFieldSelected(field)}
                        onChange={() => handleCheckboxChange(field)}
                        className="field-checkbox"
                      />
                      <div className="checkbox-item">
                        <span className="checkbox-label">{field.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dateFields.length > 0 && (
              <div className="update-multiple-fields-section">
                <h3 className="section-title">Dates</h3>
                <div className="checkbox-list">
                  {dateFields.map((field, idx) => (
                    <div
                      className="checkbox-item-container"
                      key={field.fieldKey || idx}
                    >
                      <input
                        type="checkbox"
                        checked={isFieldSelected(field)}
                        onChange={() => handleCheckboxChange(field)}
                        className="field-checkbox"
                      />
                      <div className="checkbox-item">
                        <span className="checkbox-label">{field.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {contributorFields.length > 0 && (
              <div className="update-multiple-fields-section">
                <h3 className="section-title">Contributors</h3>
                <div className="checkbox-list">
                  {contributorFields.map((field, idx) => (
                    <div
                      className="checkbox-item-container"
                      key={field.fieldKey || idx}
                    >
                      <input
                        type="checkbox"
                        checked={isFieldSelected(field)}
                        onChange={() => handleCheckboxChange(field)}
                        className="field-checkbox"
                      />
                      <div className="checkbox-item">
                        <span className="checkbox-label">{field.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {additionalFields.length > 0 && (
              <div className="update-multiple-fields-section">
                <h3 className="section-title">Additional Details</h3>
                <div className="checkbox-list">
                  {additionalFields.filter((field) => !field.label.includes("Other") && !field.label.includes("Description")).map((field, idx) => (
                    <div
                      className="checkbox-item-container"
                      key={field.fieldKey || idx}
                    >
                      <input
                        type="checkbox"
                        checked={isFieldSelected(field)}
                        onChange={() => handleCheckboxChange(field)}
                        className="field-checkbox"
                      />
                      <div className="checkbox-item">
                        <span className="checkbox-label">{field.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="update-multiple-fields-footer">
            <p className="footer-message">
              <span className="emoji-icon">😊</span> Let me know when you are
              done with the selection.
            </p>
            <button
              className="done-btn"
              onClick={handleDoneClick}
              disabled={selectedFields.length <= 1}
            >
              Action Taken
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
